import { type FC, useEffect, useRef } from 'react';
import logo from '../assets/dest.png';

// ── Leaflet types ─────────────────────────────────────────────────────────────

type LatLng = { lat: number; lng: number };

type LeafletMarker = { remove: () => void };

type LeafletMap = {
    setView: (center: LatLng, zoom: number) => void;
    fitBounds: (bounds: unknown, opts: object) => void;
};

type LeafletStatic = {
    map: (el: HTMLElement, opts: object) => LeafletMap;
    tileLayer: (url: string) => { addTo: (map: LeafletMap) => void };
    icon: (opts: object) => unknown;
    marker: (pos: LatLng, opts?: object) => LeafletMarker & { addTo: (map: LeafletMap) => LeafletMarker };
    latLngBounds: (points: LatLng[]) => unknown;
};

type WindowWithLeaflet = Window & { L?: LeafletStatic };

// ── Props ─────────────────────────────────────────────────────────────────────

interface DeliveryMapProps {
    destination?: LatLng | null;
    driver?: LatLng | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const DeliveryMap: FC<DeliveryMapProps> = ({ destination, driver }) => {
    const mapRef      = useRef<HTMLDivElement | null>(null);
    const mapInstance = useRef<LeafletMap | null>(null);
    const markersRef  = useRef<LeafletMarker[]>([]);
    const hasAnyPoint = !!destination || !!driver;

    const loadLeaflet = () =>
        new Promise<void>((resolve, reject) => {
            const w = window as WindowWithLeaflet;
            if (w.L) { resolve(); return; }

            const link = document.createElement('link');
            link.rel  = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            link.dataset.leaflet = 'true';
            document.head.appendChild(link);

            const script = document.createElement('script');
            script.src   = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.async = true;
            script.dataset.leaflet = 'true';
            script.onload  = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Leaflet'));
            document.head.appendChild(script);
        });


    useEffect(() => {
        let cancelled = false;

        const fitBoundsLocal = (L: LeafletStatic, map: LeafletMap) => {
            if (!hasAnyPoint) return;
            if (destination && driver) {
                const bounds = L.latLngBounds([destination, driver]);
                map.fitBounds(bounds, { padding: [32, 32] });
            } else {
                const center = destination ?? (driver as LatLng);
                map.setView(center, 14);
            }
        };

        const init = async () => {
            try {
                await loadLeaflet();
                if (cancelled || !mapRef.current) return;
                const L = (window as WindowWithLeaflet).L;
                if (!L) return;

                if (!mapInstance.current) {
                    mapInstance.current = L.map(mapRef.current, {
                        center: destination ?? driver ?? { lat: 0, lng: 0 },
                        zoom: 13,
                    });
                    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(mapInstance.current);
                }

                markersRef.current.forEach(m => m.remove());
                markersRef.current = [];

                const icon = L.icon({ iconUrl: logo, iconSize: [41, 41], iconAnchor: [12, 41] });
                if (destination) markersRef.current.push(L.marker(destination, { icon }).addTo(mapInstance.current));
                if (driver)      markersRef.current.push(L.marker(driver,      { icon }).addTo(mapInstance.current));

                fitBoundsLocal(L, mapInstance.current);
            } catch { /* Leaflet errors are non-fatal */ }
        };

        void init();
        return () => { cancelled = true; };
    }, [destination, driver, hasAnyPoint]);

    return (
        <div className="w-full h-80 rounded-2xl ring-1 ring-stone-200 overflow-hidden relative bg-white">
            <div ref={mapRef} className="w-full h-full" />
            {!hasAnyPoint && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-sm text-gray-600">
                    Location data is not available.
                </div>
            )}
        </div>
    );
};